import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faGlobe,
  faSpinner,
  faTriangleExclamation,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import CoverageArt from '../auth/CoverageArt';
import { useCurrency } from '../../context/CurrencyContext';
import { CURRENCIES, formatPrice, minPlanPrices } from '../../utils/currency';
import { getCountries, getContinents, getHomepage } from './homeApi';
import { loadUnlimitedBundles, resetUnlimitedBundles } from './unlimitedPlans';
import {
  loadCountryVoiceBundles,
  loadRegionalVoiceBundles,
  resetVoiceBundles,
} from './voicePlans';
import './home.css';

const TABS = [
  { key: 'local', labelKey: 'home.tabs.local' },
  { key: 'regional', labelKey: 'home.tabs.regional' },
  { key: 'lifetime', labelKey: 'home.tabs.lifetime' },
  { key: 'unlimited', labelKey: 'home.tabs.unlimited' },
  { key: 'voice', labelKey: 'home.tabs.voice' },
];

// Bölgesel ve ömürlük iki ayrı katalogdur. Hangi kıtanın hangisinde görüneceğini
// backend'deki isActiveIntercontinental / isActiveLifetime bayrakları belirler —
// admin panelden yönetilir, mobil uygulama da tam olarak bunları kullanır.
//
// Sınırsız ve ses paketleri bu katalogların hiçbirinde yok; ayrı ürün aileleri
// olarak /v2/bundles?family=unlimited|voice ucundan gelir
// (src/FRONTEND_INTEGRATION.md §1, docs/ses-paketleri.md).
const fetchers = {
  local: getCountries,
  regional: () =>
    getContinents().then((list) =>
      list.filter((c) => c.isActiveIntercontinental === true)
    ),
  lifetime: () =>
    getContinents().then((list) => list.filter((c) => c.isActiveLifetime === true)),
  unlimited: loadUnlimitedBundles,
  // Ses sekmesi yalnızca tek-ülke paketlerini listeler; çok ülkeli olan Hercules
  // kendi sayfasında (/esim/hercules) durur, aşağıdaki öne çıkan kart oraya
  // götürür (bkz. docs/hercules-sayfasi.md).
  voice: loadCountryVoiceBundles,
};

// Kıta kartlarının gideceği detay rotası sekmeye göre değişir.
const REGION_ROUTE = { regional: 'region', lifetime: 'lifetime' };

// Bir ülkenin paketleri içindeki en düşük (pozitif) fiyatı her para biriminde.
const minByCurrency = (products) => {
  const out = {};
  CURRENCIES.forEach((c) => {
    let min = null;
    (products || []).forEach((p) =>
      (p.refills || []).forEach((r) => {
        const v = parseFloat(r[c.priceField]);
        if (!Number.isNaN(v) && v > 0) min = min == null || v < min ? v : min;
      })
    );
    out[c.code] = min;
  });
  return out;
};

// Her sekmenin ham verisini ortak karta indirger.
const normalize = (tab, items) => {
  if (!Array.isArray(items)) return [];
  if (tab === 'local') {
    return items
      .filter((c) => c.isActive !== false)
      .map((c) => ({
        key: c.country,
        name: c.countrySelected || c.country,
        alt: c.country,
        flag: c.flag,
        prices: minByCurrency(c.products),
        to: `/esim/country/${encodeURIComponent(c.country)}`,
      }));
  }
  if (tab === 'unlimited' || tab === 'voice') {
    // Kart = bundle (/v2/bundles). Arama, bundle başlığının yanında kapsanan
    // ülke adlarında da eşleşsin diye `alt`'a ülkeler yazılır (ör. "Portugal
    // Unlimited" ararken "Portekiz" de bulunsun).
    return items.map((b) => ({
      key: b.id,
      name: b.title,
      alt: [
        ...(b.countries || []).flatMap((c) => [c.name, c.nameSelected]),
        ...(b.regions || []),
      ]
        .filter(Boolean)
        .join(' '),
      flag: b.imgUrl,
      prices: minPlanPrices(b.plans),
      to: `/esim/${tab}/${b.id}`,
    }));
  }
  // regional / lifetime → kıtalar. imgUrl panelden yüklenen kıta görselidir ve
  // null olabilir; o zaman <Flag> dünya ikonuna düşer. Bayrak oranında değil
  // (kare/geniş) — `art` işareti kırpma yerine sığdırma stilini seçtirir.
  return items.map((c) => ({
    key: c.id,
    name: c.name,
    alt: c.name,
    flag: c.imgUrl || null,
    art: true,
    price: null,
    to: `/esim/${REGION_ROUTE[tab]}/${c.id}`,
  }));
};

// art=true → bayrak değil, serbest oranlı görsel (kıta illüstrasyonu): kırpmak
// yerine kutuya sığdırılır.
const Flag = ({ src, alt, art }) => {
  const [failed, setFailed] = useState(!src);
  if (failed) {
    return (
      <span className="card-flag card-flag--fallback">
        <FontAwesomeIcon icon={faGlobe} />
      </span>
    );
  }
  return (
    <img
      className={art ? 'card-flag card-flag--art' : 'card-flag'}
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const { currency } = useCurrency();
  const [activeTab, setActiveTab] = useState('local');
  const [query, setQuery] = useState('');
  const [cache, setCache] = useState({});
  const [popular, setPopular] = useState([]);
  // /v2/bundles sekmeleri (sınırsız, ses) yalnızca katalogda en az bir paket
  // varken görünür; boş ya da erişilemeyen katalog için ölü sekme göstermiyoruz.
  // Kataloglar modül düzeyinde önbellekli olduğundan bu ön-yükleme, sekmeye
  // tıklanınca atılacak isteği de karşılar (ikinci istek çıkmaz).
  const [available, setAvailable] = useState({ unlimited: false, voice: false });
  // Hercules öne çıkan kartı: ürün pasife alınırsa kart kendiliğinden kaybolur.
  const [hercules, setHercules] = useState(null);
  const inFlight = useRef({});

  const current = cache[activeTab] || {};

  useEffect(() => {
    let cancelled = false;
    const probe = (key, load) =>
      load()
        .then((list) => (list || []).length > 0)
        .catch(() => false)
        .then((ok) => {
          if (!cancelled) setAvailable((a) => (a[key] === ok ? a : { ...a, [key]: ok }));
        });
    probe('unlimited', loadUnlimitedBundles);
    probe('voice', loadCountryVoiceBundles);

    // Aynı ses kataloğundan süzülür; ek istek çıkmaz.
    loadRegionalVoiceBundles()
      .then((list) => !cancelled && setHercules(list[0] || null))
      .catch(() => !cancelled && setHercules(null));

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const isTabVisible = (key) => (key in available ? available[key] : true);

  // Sekme açıkken katalog kaybolursa (ör. dil değişiminde yeniden çekim
  // başarısız olursa) kullanıcıyı görünmez bir sekmede bırakma.
  useEffect(() => {
    if (activeTab in available && !available[activeTab]) setActiveTab('local');
  }, [available, activeTab]);

  const visibleTabs = TABS.filter((tab) => isTabVisible(tab.key));

  // Sekme verisi (önbellekli, StrictMode'a dayanıklı).
  useEffect(() => {
    const tab = activeTab;
    if (cache[tab]?.data || cache[tab]?.error || inFlight.current[tab]) return;
    inFlight.current[tab] = true;
    setCache((c) => ({ ...c, [tab]: { loading: true } }));
    fetchers[tab]()
      .then((data) => setCache((c) => ({ ...c, [tab]: { data: normalize(tab, data) } })))
      .catch((err) => setCache((c) => ({ ...c, [tab]: { error: err.message } })))
      .finally(() => {
        inFlight.current[tab] = false;
      });
  }, [activeTab, cache]);

  // Popüler destinasyon çipleri (hero).
  useEffect(() => {
    let cancelled = false;
    getHomepage()
      .then((d) => {
        if (!cancelled) setPopular((d?.popularCountries || []).slice(0, 8));
      })
      .catch((err) => {
        // Popüler çipler dekoratiftir; hata sayfayı bozmaz ama sessiz kalmasın.
        console.warn('Popüler destinasyonlar yüklenemedi:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const items = current.data || [];
    const q = query.trim().toLocaleLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.name?.toLocaleLowerCase().includes(q) ||
        i.alt?.toLocaleLowerCase().includes(q)
    );
  }, [current.data, query]);

  const retry = () => {
    // /v2/bundles katalogları modül düzeyinde önbelleklendiği için sekme
    // önbelleğini silmek yetmez; hatalı sonucu orada da temizle.
    if (activeTab === 'unlimited') resetUnlimitedBundles();
    if (activeTab === 'voice') resetVoiceBundles();
    setCache((c) => {
      const next = { ...c };
      delete next[activeTab];
      return next;
    });
  };

  return (
    <div className="home">
      <StoreHeader />

      {/* Hero */}
      <section className="home-hero">
        <CoverageArt />
        <div className="home-hero-inner">
          <p className="home-eyebrow">
            <span className="signal-dot" />
            {t('home.coverage')}
          </p>
          <h1 className="home-hero-title">
            {t('home.hero.title1')}
            <br />
            <span className="home-hero-accent">{t('home.hero.title2')}</span>
          </h1>
          <p className="home-hero-subtitle">{t('home.hero.subtitle')}</p>

          <div className="home-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="home-search-icon" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('home.search.placeholder')}
              aria-label={t('home.search.placeholder')}
            />
          </div>

          {popular.length > 0 && (
            <div className="home-chips">
              <span className="home-chips-label">{t('home.popular')}</span>
              <div className="home-chips-row">
                {popular.map((p) => (
                  <Link
                    key={p.countryNameId}
                    to={`/esim/country/${encodeURIComponent(p.countryNameId)}`}
                    state={{ name: p.countryName, flag: p.flag }}
                    className="home-chip"
                  >
                    <img src={p.flag} alt="" loading="lazy" />
                    {p.countryName}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Katalog */}
      <section className="home-catalog">
        <div className="home-tabs" role="tablist">
          {visibleTabs.map((tab) => {
            const count = cache[tab.key]?.data?.length;
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`home-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {t(tab.labelKey)}
                {count != null && <span className="home-tab-count">{count}</span>}
              </button>
            );
          })}
        </div>

        {/* Hercules'e giriş noktası. Ses sekmesinde: listeden çıkarıldığı için
            görünür kalmalı. Bölgesel sekmesinde: müşteri onu orada aradı ve
            bulamadı ("çıkmıyor" şikâyeti) — kart doğrudan oraya götürür.
            Aramada gizlenir, sonuçları bozmasın. */}
        {hercules && !query.trim() && (activeTab === 'voice' || activeTab === 'regional') && (
          <Link to="/esim/hercules" className="feature-card">
            {hercules.imgUrl && (
              <img className="feature-card-img" src={hercules.imgUrl} alt="" />
            )}
            <span className="feature-card-body">
              <span className="feature-card-title">
                {hercules.title}
                <span className="detail-badge">{t('detail.voice')}</span>
              </span>
              <span className="feature-card-desc">
                {t('hercules.promo.desc', { n: (hercules.countries || []).length })}
              </span>
            </span>
            <span className="feature-card-cta">
              {t('hercules.promo.cta')}
              <FontAwesomeIcon icon={faArrowRight} />
            </span>
          </Link>
        )}

        {current.loading && (
          <div className="home-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {current.error && (
          <div className="home-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{t('home.error.title')}</p>
            <button className="home-retry" onClick={retry}>
              {t('home.error.retry')}
            </button>
          </div>
        )}

        {!current.loading && !current.error && filtered.length === 0 && (
          <div className="home-state">
            <p>{t('home.empty')}</p>
          </div>
        )}

        {!current.loading && !current.error && filtered.length > 0 && (
          <div className="home-grid">
            {filtered.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                state={{ name: item.name, flag: item.flag }}
                className="esim-card"
              >
                <Flag src={item.flag} alt={item.alt} art={item.art} />
                <span className="esim-card-body">
                  <span className="esim-card-name">{item.name}</span>
                  {item.prices?.[currency] != null && (
                    <span className="esim-card-price">
                      <span className="from">{t('home.from')}</span>
                      {formatPrice(item.prices[currency], currency)}
                    </span>
                  )}
                </span>
                <FontAwesomeIcon icon={faArrowRight} className="esim-card-arrow" />
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default Home;
